(() => {
  if (customElements.get('ss-signature-launch')) return;

  const FRAMES = 36;
  const SHEETS = 4;
  const GRID = 3;
  const SLOTS = [
    // x, y (% of the bottle box), longest side (% of box height), rotation, parallax depth, behind bottle, blur, opacity
    { x: 10, y: 30, s: 31, r: -14, z: 1.25, back: false, b: 0 },
    { x: 84, y: 23, s: 30, r: 14, z: 0.7, back: true, b: 0 },
    { x: 7, y: 71, s: 33, r: 8, z: 1.45, back: false, b: 0 },
    { x: 94, y: 67, s: 34, r: -10, z: 1.2, back: false, b: 0 },
    { x: 104, y: 94, s: 12, r: 24, z: 1.9, back: false, b: 1.5, o: 0.85 },
    { x: -8, y: 50, s: 11, r: -30, z: 0.5, back: true, b: 1, o: 0.7 },
    { x: 6, y: 76, s: 19, r: 18, z: 1.6, back: false, b: 0.5, o: 0.95 }
  ];
  const ease = t => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
  const easeOut = t => 1 - Math.pow(1 - t, 3);
  const mod = (n, m) => ((n % m) + m) % m;

  class SSSignatureLaunch extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.signal = this.controller.signal;
      this.portal = this.closest('ss-incense-portal');
      this.stage = this.querySelector('.ssl-stage');
      this.tilt = this.querySelector('.ssl-tilt');
      this.canvas = this.querySelector('.ssl-canvas');
      this.ctx = this.canvas?.getContext('2d');
      this.spin = this.querySelector('.ssl-spin');
      this.status = this.querySelector('[data-launch-status]');
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.panels = Array.from(this.querySelectorAll('.ssl-panel'));
      this.posters = Array.from(this.querySelectorAll('.ssl-frame'));
      this.noteSets = Array.from(this.querySelectorAll('.ssl-noteset'));
      this.reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.variants = this.tabs.map((tab, index) => ({ index, url: tab.dataset.frames || '', color: tab.dataset.color, name: tab.dataset.name || tab.textContent.trim(), sheets: new Array(SHEETS), loading: null, complete: false }));
      this.current = Math.max(0, this.tabs.findIndex(tab => tab.getAttribute('aria-selected') === 'true'));
      this.pos = 0;
      this.shown = -1;
      this.velocity = 0;
      this.sweep = -1;
      this.px = 0; this.py = 0; this.tx = 0; this.ty = 0;
      this.backdrop = this.parentElement?.querySelector('.ssl-backdrop');
      this.layoutNotes();
      this.bind();
      if (this.backdrop) {
        this.resizeObserver = new ResizeObserver(() => this.shapeBackdrop());
        this.resizeObserver.observe(this.backdrop);
        this.shapeBackdrop();
      }
      this.noteSets.forEach((set, index) => set.classList.toggle('is-on', index === this.current));
      this.visibleObserver = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting;
        if (this.visible) { this.begin(); this.wake(); } else this.sleep();
      }, { rootMargin: '120px' });
      this.visibleObserver.observe(this);
      this.classObserver = new MutationObserver(() => this.wake());
      if (this.portal) this.classObserver.observe(this.portal, { attributes: true, attributeFilter: ['class'] });
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.visibleObserver?.disconnect();
      this.classObserver?.disconnect();
      this.resizeObserver?.disconnect();
      this.sleep();
      clearTimeout(this.revealTimer);
      clearTimeout(this.sweepTimer);
      clearTimeout(this.idleTimer);
      clearTimeout(this.swapTimer);
    }

    get paused() { return !!this.portal?.classList.contains('ss-paused') || this.reduce.matches; }

    listen(target, type, handler, options = {}) { target?.addEventListener(type, handler, { ...options, signal: this.signal }); }

    bind() {
      this.tabs.forEach((tab, index) => {
        this.listen(tab, 'click', () => this.select(index));
        this.listen(tab, 'pointerenter', () => this.prefetch(index), { passive: true });
        this.listen(tab, 'focus', () => this.prefetch(index));
        this.listen(tab, 'keydown', event => {
          const keys = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
          let next = null;
          if (event.key in keys) next = mod(index + keys[event.key], this.tabs.length);
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = this.tabs.length - 1;
          if (next == null) return;
          event.preventDefault();
          this.tabs[next].focus();
          this.select(next);
        });
      });
      if (this.spin) {
        this.listen(this.spin, 'pointerdown', event => this.dragStart(event));
        this.listen(this.spin, 'pointermove', event => this.dragMove(event));
        this.listen(this.spin, 'pointerup', event => this.dragEnd(event));
        this.listen(this.spin, 'pointercancel', event => this.dragEnd(event));
        this.listen(this.spin, 'lostpointercapture', event => this.dragEnd(event));
        this.listen(this.spin, 'keydown', event => {
          const step = { ArrowRight: 1, ArrowLeft: -1 }[event.key];
          if (step) { event.preventDefault(); this.turnTo(Math.round(this.pos) + step * 3, 380); this.markSpun(); }
          if (event.key === 'Home') { event.preventDefault(); this.turnTo(Math.round(this.pos / FRAMES) * FRAMES, 600); }
        });
      }
      const host = this.portal?.querySelector('.ss-scene') || this;
      this.listen(host, 'pointermove', event => {
        if (event.pointerType !== 'mouse' || this.paused) return;
        const rect = host.getBoundingClientRect();
        this.tx = ((event.clientX - rect.left) / rect.width - 0.5) * 2;
        this.ty = ((event.clientY - rect.top) / rect.height - 0.5) * 2;
        this.wake();
      }, { passive: true });
      this.listen(host, 'pointerleave', () => { this.tx = 0; this.ty = 0; this.wake(); });
      this.listen(this.reduce, 'change', () => this.wake());
      this.listen(document, 'visibilitychange', () => (document.hidden ? this.sleep() : this.wake()));
    }

    layoutNotes() {
      this.noteSets.forEach(set => {
        const mains = Array.from(set.querySelectorAll('.ssl-note')).slice(0, 4);
        mains.slice(0, 2).forEach(item => {
          if (mains.length < 2) return;
          const echo = item.cloneNode(true);
          echo.dataset.echo = '1';
          echo.querySelector('img')?.setAttribute('loading', 'lazy');
          set.appendChild(echo);
        });
        Array.from(set.querySelectorAll('.ssl-note')).forEach(item => {
          const image = item.querySelector('img');
          if (!image) return;
          const fit = () => { if (image.naturalWidth) item.style.setProperty('--k', Math.min(1, image.naturalWidth / image.naturalHeight).toFixed(3)); };
          const drop = () => { item.remove(); this.placeNotes(set); };
          if (image.complete && !image.naturalWidth) { item.remove(); return; }
          if (image.complete) fit(); else image.addEventListener('load', fit, { once: true });
          image.addEventListener('error', drop, { once: true });
        });
        this.placeNotes(set);
      });
    }

    // Where the calligraphy may show: the wall around the arch and, more softly, the door panel inside it.
    // Uses the same framing as the arch drawing (a 440 x 576 box fitted and centred in the scene); the frame itself stays clear.
    shapeBackdrop() {
      const w = Math.round(this.backdrop.clientWidth);
      const h = Math.round(this.backdrop.clientHeight);
      if (!w || !h || (w === this.shapeW && h === this.shapeH)) return;
      this.shapeW = w;
      this.shapeH = h;
      const s = Math.min(w / 440, h / 576);
      const ox = (w - 440 * s) / 2;
      const oy = (h - 576 * s) / 2;
      const bottom = (h - oy) / s + 66;
      const p = (x, y) => (ox + x * s).toFixed(1) + ' ' + (oy + (y - 64) * s).toFixed(1);
      const arch = (l, r, shoulder, c1, c2, top) => `M${p(l, bottom)}L${p(l, shoulder)}C${p(l, c1)} ${p(220 - (220 - l) * 0.27, c2)} ${p(220, top)}C${p(220 + (r - 220) * 0.27, c2)} ${p(r, c1)} ${p(r, shoulder)}L${p(r, bottom)}Z`;
      const outer = arch(34, 406, 272, 150, 158, 48);
      const inner = arch(118, 322, 342, 266, 266, 202);
      const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}"><path fill-rule="evenodd" d="M0 0H${w}V${h}H0Z${outer}"/><path fill-opacity=".6" d="${inner}"/></svg>`;
      this.backdrop.style.setProperty('--ssl-shape', `url("data:image/svg+xml,${encodeURIComponent(svg)}")`);
    }

    placeNotes(set) {
      const items = Array.from(set.querySelectorAll('.ssl-note'));
      const mains = items.filter(item => !item.dataset.echo);
      const echoes = items.filter(item => item.dataset.echo);
      const plan = { 4: [[0, 1, 2, 3], [4, 5]], 3: [[0, 3, 1], [6, 5]], 2: [[0, 3], [6, 1]], 1: [[0], [3]] }[Math.min(4, mains.length)] || [[], []];
      const place = (item, index, order) => {
        const slot = SLOTS[index];
        if (!slot) { item.hidden = true; return; }
        item.hidden = false;
        item.style.cssText = `--x:${slot.x}%;--y:${slot.y}%;--s:${slot.s};--r:${slot.r}deg;--b:${slot.b}px;--o:${slot.o ?? 1};--d:${6 + (order % 3) * 1.4}s;--dd:${-order * 1.3}s` + (item.style.getPropertyValue('--k') ? `;--k:${item.style.getPropertyValue('--k')}` : '');
        item.dataset.depth = slot.z;
        item.classList.toggle('is-back', slot.back);
      };
      mains.forEach((item, i) => place(item, plan[0][i], i));
      echoes.forEach((item, i) => place(item, plan[1][i], mains.length + i));
    }

    begin() {
      if (this.started) return;
      this.started = true;
      const poster = this.posters[this.current];
      const ready = poster && !poster.complete ? new Promise(resolve => { poster.addEventListener('load', resolve, { once: true }); poster.addEventListener('error', resolve, { once: true }); }) : Promise.resolve();
      const delay = this.reduce.matches ? 0 : 1450;
      const started = performance.now();
      ready.then(() => {
        const wait = Math.max(0, delay - (performance.now() - started));
        this.revealTimer = setTimeout(() => {
          this.classList.add('is-revealed');
          const variant = this.variants[this.current];
          this.load(this.current).then(() => {
            if (!this.isConnected) return;
            this.useCanvas();
            this.scheduleSweep(3200);
            const full = Promise.race([variant.completed || Promise.resolve(), new Promise(resolve => setTimeout(resolve, 2600))]);
            full.then(() => {
              this.idleTimer = setTimeout(() => this.posters.forEach(poster => { if (poster.loading === 'lazy') poster.loading = 'eager'; }), 2500);
              if (this.paused || this.userTurned || !this.isConnected) return;
              this.pos = -FRAMES; this.draw(true); this.turnTo(0, 2600);
            });
          });
        }, wait);
      });
    }

    sheetUrl(variant, index) {
      if (!variant.url) return '';
      const height = (this.stage?.clientHeight || 600) * Math.min(window.devicePixelRatio || 1, 2);
      const width = Math.min(1620, Math.max(720, Math.ceil((height * (2 / 3) * GRID) / 180) * 180));
      const file = variant.url.replace(/-s0(\.[a-z0-9]+)/i, '-s' + index + '$1');
      return file + (file.includes('?') ? '&' : '?') + 'width=' + width;
    }

    frame(variant, index) {
      const sheet = variant.sheets[index % SHEETS];
      if (!sheet) return null;
      const cell = Math.floor(index / SHEETS);
      const w = sheet.naturalWidth / GRID;
      const h = sheet.naturalHeight / GRID;
      return { image: sheet, sx: (cell % GRID) * w, sy: Math.floor(cell / GRID) * h, w, h };
    }

    load(index) {
      const variant = this.variants[index];
      if (!variant || !variant.url) return Promise.resolve();
      if (variant.loading) return variant.loading;
      variant.loading = new Promise(resolve => {
        const get = sheetIndex => new Promise(finish => {
          const image = new Image();
          image.decoding = 'async';
          image.src = this.sheetUrl(variant, sheetIndex);
          let settled = false;
          const ok = () => { if (settled) return; settled = true; variant.sheets[sheetIndex] = image; if (index === this.current) this.draw(true); finish(); };
          const fail = () => { if (settled) return; settled = true; finish(); };
          image.onerror = fail;
          image.onload = () => {
            if (!image.decode) return ok();
            Promise.race([image.decode().catch(() => {}), new Promise(resolve => setTimeout(resolve, 1200))]).then(ok);
          };
        });
        get(0).then(() => {
          variant.ready = true;
          resolve();
          variant.completed = Promise.all([1, 2, 3].map(get)).then(() => { variant.complete = true; });
        });
      });
      return variant.loading;
    }

    prefetch(index) {
      if (index === this.current) return;
      const poster = this.posters[index];
      if (poster && poster.loading === 'lazy') poster.loading = 'eager';
      this.eagerNotes(index);
      this.load(index);
    }

    eagerNotes(index) {
      this.noteSets[index]?.querySelectorAll('img[loading="lazy"]').forEach(image => { image.loading = 'eager'; });
    }

    useCanvas() {
      const variant = this.variants[this.current];
      const first = this.frame(variant, 0);
      if (!this.canvas || !first) return;
      const cw = Math.round(first.w), ch = Math.round(first.h);
      if (this.canvas.width !== cw || this.canvas.height !== ch) { this.canvas.width = cw; this.canvas.height = ch; }
      this.canvas.hidden = false;
      this.classList.add('has-canvas');
      this.shown = -1;
      this.draw(true);
    }

    draw(force) {
      if (!this.ctx || this.canvas.hidden) return;
      const variant = this.variants[this.current];
      const frame = mod(Math.round(this.pos), FRAMES);
      let cell = this.frame(variant, frame);
      for (let offset = 1; !cell && offset < SHEETS; offset++) cell = this.frame(variant, mod(frame - offset, FRAMES)) || this.frame(variant, mod(frame + offset, FRAMES));
      if (!cell) return;
      const key = frame + ':' + this.current + ':' + (this.sweep >= 0 ? this.sweep.toFixed(3) : '');
      if (!force && key === this.shown) return;
      this.shown = key;
      const { width, height } = this.canvas;
      const ctx = this.ctx;
      ctx.globalCompositeOperation = 'source-over';
      ctx.clearRect(0, 0, width, height);
      ctx.drawImage(cell.image, cell.sx, cell.sy, cell.w, cell.h, 0, 0, width, height);
      if (this.sweep >= 0) {
        const t = this.sweep;
        const x = -0.6 * width + t * 2.2 * width;
        const gradient = ctx.createLinearGradient(x - width * 0.35, height * 0.1, x + width * 0.35, height * 0.9);
        gradient.addColorStop(0, 'rgba(255,244,214,0)');
        gradient.addColorStop(0.46, 'rgba(255,244,214,0.12)');
        gradient.addColorStop(0.5, 'rgba(255,250,236,0.55)');
        gradient.addColorStop(0.54, 'rgba(255,244,214,0.12)');
        gradient.addColorStop(1, 'rgba(255,244,214,0)');
        ctx.globalCompositeOperation = 'source-atop';
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, width, height);
        ctx.globalCompositeOperation = 'source-over';
      }
    }

    scheduleSweep(delay) {
      clearTimeout(this.sweepTimer);
      this.sweepTimer = setTimeout(() => {
        if (!this.controller) return;
        if (this.visible && !this.paused && !document.hidden && !this.canvas.hidden) { this.sweepStart = performance.now(); this.wake(); }
        this.scheduleSweep(6500 + Math.random() * 2500);
      }, delay);
    }

    turnTo(target, duration) {
      this.anim = { from: this.pos, to: target, start: performance.now(), duration, curve: duration > 900 ? ease : easeOut };
      this.velocity = 0;
      this.wake();
    }

    markSpun() {
      this.userTurned = true;
      this.classList.add('has-spun');
    }

    dragStart(event) {
      if (event.button > 0) return;
      this.drag = { id: event.pointerId, x: event.clientX, y: event.clientY, pos: this.pos, last: event.clientX, time: performance.now(), moved: false, locked: event.pointerType === 'mouse' };
      this.velocity = 0;
      this.anim = null;
      if (this.drag.locked) this.spin.setPointerCapture(event.pointerId);
    }

    dragMove(event) {
      const drag = this.drag;
      if (!drag || drag.id !== event.pointerId) return;
      const dx = event.clientX - drag.x;
      const dy = event.clientY - drag.y;
      if (!drag.locked) {
        if (Math.abs(dy) > 10 && Math.abs(dy) > Math.abs(dx)) { this.drag = null; return; }
        if (Math.abs(dx) < 6) return;
        drag.locked = true;
        this.spin.setPointerCapture(event.pointerId);
      }
      event.preventDefault();
      const perFrame = Math.max(6, this.spin.clientWidth / 18);
      const now = performance.now();
      const step = (event.clientX - drag.last) / perFrame;
      const dt = Math.max(8, now - drag.time);
      this.velocity = this.velocity * 0.6 + (step / dt) * 16 * 0.4;
      drag.last = event.clientX;
      drag.time = now;
      drag.moved = true;
      this.pos = drag.pos + dx / perFrame;
      this.markSpun();
      this.draw();
    }

    dragEnd(event) {
      const drag = this.drag;
      if (!drag || drag.id !== event.pointerId) return;
      this.drag = null;
      if (this.spin.hasPointerCapture?.(event.pointerId)) this.spin.releasePointerCapture(event.pointerId);
      if (!drag.moved) return;
      if (performance.now() - drag.time > 90) this.velocity = 0;
      this.wake();
    }

    select(index) {
      if (index === this.current || !this.tabs[index]) return;
      const previous = this.current;
      this.current = index;
      this.tabs.forEach((tab, i) => { tab.setAttribute('aria-selected', String(i === index)); tab.tabIndex = i === index ? 0 : -1; });
      this.panels.forEach((panel, i) => {
        panel.hidden = i !== index;
        panel.classList.toggle('is-entering', i === index && !this.reduce.matches);
      });
      this.noteSets.forEach((set, i) => {
        set.classList.toggle('is-on', i === index);
        set.classList.toggle('is-leaving', i === previous && !this.reduce.matches);
        if (i === previous) setTimeout(() => set.classList.remove('is-leaving'), 700);
      });
      const variant = this.variants[index];
      this.style.setProperty('--ssl-accent', variant.color || '#d7a43b');
      this.backdrop?.style.setProperty('--ssl-accent', variant.color || '#d7a43b');
      this.posters.forEach((poster, i) => { poster.hidden = i !== index; if (i === index && poster.loading === 'lazy') poster.loading = 'eager'; });
      this.eagerNotes(index);
      if (this.status) this.status.textContent = variant.name;
      this.canvas.hidden = true;
      this.classList.remove('has-canvas');
      if (this.reduce.matches) {
        this.load(index).then(() => { if (this.current === index) { this.pos = 0; this.useCanvas(); } });
        return;
      }
      this.classList.add('is-swapping');
      clearTimeout(this.swapTimer);
      this.swapTimer = setTimeout(() => this.classList.remove('is-swapping'), 420);
      const token = this.selectToken = (this.selectToken || 0) + 1;
      const ready = variant.ready ? Promise.resolve() : Promise.race([this.load(index), new Promise(resolve => setTimeout(resolve, 1400))]);
      let waited = false;
      const show = () => {
        if (this.current !== index || this.selectToken !== token || !this.isConnected) return;
        if (!variant.sheets.some(Boolean)) { if (!waited && variant.loading) { waited = true; variant.loading.then(show); } return; }
        this.useCanvas();
        this.pos = Math.round(this.pos / FRAMES) * FRAMES - FRAMES / 2;
        this.draw(true);
        this.turnTo(this.pos + FRAMES / 2, 1300);
        variant.completed?.then(() => { if (this.current === index) this.draw(true); });
      };
      ready.then(show);
    }

    wake() {
      if (this.frameId || !this.controller || !this.visible || document.hidden) return;
      this.last = performance.now();
      this.frameId = requestAnimationFrame(now => this.tick(now));
    }

    sleep() {
      cancelAnimationFrame(this.frameId);
      this.frameId = null;
    }

    tick(now) {
      this.frameId = null;
      const dt = Math.min(0.05, (now - this.last) / 1000);
      this.last = now;
      let busy = false;
      if (this.anim) {
        const t = Math.min(1, (now - this.anim.start) / this.anim.duration);
        this.pos = this.anim.from + (this.anim.to - this.anim.from) * this.anim.curve(t);
        if (t >= 1) { this.pos = this.anim.to; this.anim = null; }
        busy = true;
      } else if (!this.drag && Math.abs(this.velocity) > 0.02) {
        this.pos += this.velocity * dt * 60;
        this.velocity *= Math.pow(0.9, dt * 60);
        busy = true;
      } else if (!this.drag && Math.abs(this.pos - Math.round(this.pos)) > 0.001) {
        this.pos += (Math.round(this.pos) - this.pos) * Math.min(1, dt * 14);
        busy = true;
      }
      if (this.sweepStart) {
        const t = (now - this.sweepStart) / 1250;
        if (t >= 1) { this.sweep = -1; this.sweepStart = 0; this.shown = -1; }
        else { this.sweep = easeOut(t); busy = true; }
      }
      this.draw();
      const motion = !this.paused;
      const tx = motion ? this.tx : 0;
      const ty = motion ? this.ty : 0;
      const k = 1 - Math.exp(-dt * 5);
      this.px += (tx - this.px) * k;
      this.py += (ty - this.py) * k;
      if (Math.abs(tx - this.px) + Math.abs(ty - this.py) > 0.002) busy = true;
      const set = this.noteSets[this.current];
      if (set) {
        set.querySelectorAll('.ssl-note').forEach(note => {
          const depth = parseFloat(note.dataset.depth) || 1;
          note.style.setProperty('--px', (-this.px * 14 * depth).toFixed(2) + 'px');
          note.style.setProperty('--py', (-this.py * 10 * depth).toFixed(2) + 'px');
        });
      }
      if (this.tilt) this.tilt.style.translate = (this.px * 6).toFixed(2) + 'px ' + (this.py * 4).toFixed(2) + 'px';
      if (busy) this.frameId = requestAnimationFrame(time => this.tick(time));
    }
  }

  customElements.define('ss-signature-launch', SSSignatureLaunch);
})();